import { Directive, HostBinding, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormControlStatus, NgControl, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { mapValues } from 'lodash';
import { Subscription } from 'rxjs';
import { AppConfigService } from '../../app-config.service';
import { DesjardinsCurrencyPipe } from '../../shared/pipes/dd-currency.pipe';
import { FormErrorValidationContextDirective } from './form-error-validation-context.directive';

const DEFAULT_CONTEXT = 'erreur.validators';
const MESSAGE_VIDE = ' ';

/**
 * Contruit les messages d'erreurs à partir des erreurs de
 * validation du FormControl et injecte le message traduit dans
 * l'élément sur lequel il est appliqué
 *
 *  Exemple:
 *
 * ```html
 * <dsd-input [formControl]="formControl">
 *  <span slot="error" dsdExtFormErrorMessage></span>
 * </dsd-input>
 * ```
 *  Devient
 * ```html
 *  <span slot="error">Le montant doit être supérieur à 15 000$</span>
 * ```
 **/
@Directive({
  selector: '[dsdExtFormErrorMessage]',
  standalone: false
})
export class FormErrorMessageDirective implements OnInit, OnDestroy {
  errorMessage = MESSAGE_VIDE;
  statusSubscription: Subscription;

  @HostBinding()
  get innerText() {
    return this.errorMessage;
  }

  constructor(
    private readonly control: NgControl,
    private readonly translateService: TranslateService,
    private readonly appConfigService: AppConfigService,
    @Optional() private readonly validationContext: FormErrorValidationContextDirective
  ) {}

  ngOnInit() {
    if (this.control.errors) {
      this.showError();
    }

    this.statusSubscription = this.control.statusChanges.subscribe(status => this.handleStatusChange(status));
  }

  ngOnDestroy() {
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
  }

  private handleStatusChange(status: FormControlStatus) {
    if (status === 'VALID') {
      this.removeError();
    } else if (status === 'INVALID') {
      this.showError();
    }
  }

  private removeError() {
    this.errorMessage = MESSAGE_VIDE;
  }

  private showError() {
    this.errorMessage = this.translateErrorMessage(this.control.errors);
  }

  private translateErrorMessage(errors: ValidationErrors) {
    if (!errors || Object.keys(errors).length === 0) {
      return MESSAGE_VIDE;
    }

    const [errorKey, errorParams] = Object.entries(errors)[0];
    const context = this.validationContext ? this.validationContext.context : DEFAULT_CONTEXT;
    return this.translateService.instant(`${context}.${errorKey}`, this.formatParams(errorParams));
  }

  private formatParams(params) {
    const pipe = new DesjardinsCurrencyPipe(this.appConfigService);
    return mapValues(params, value => (value ? pipe.transform(value) : null));
  }
}
